import { apiClient } from '../services/apiClient';
import { ENDPOINTS } from '../constants/endpoints';

// User Management
export const getUsers = (params) => apiClient.get(ENDPOINTS.ADMIN.USERS, { params });
export const getUserById = (userId) => apiClient.get(ENDPOINTS.ADMIN.USER(userId));
export const updateUser = (userId, data) => apiClient.put(ENDPOINTS.ADMIN.USER(userId), data);
export const deleteUser = (userId) => apiClient.delete(ENDPOINTS.ADMIN.USER(userId));

// Registration Management
export const getRegistrations = (params) => apiClient.get(ENDPOINTS.ADMIN.REGISTRATIONS, { params });
export const getRegistrationById = (registrationId) => apiClient.get(ENDPOINTS.ADMIN.REGISTRATION(registrationId));
export const createRegistration = (data) => apiClient.post(ENDPOINTS.ADMIN.REGISTRATIONS, data);
export const updateRegistration = (registrationId, data) => apiClient.put(ENDPOINTS.ADMIN.REGISTRATION(registrationId), data);
export const deleteRegistration = (registrationId) => apiClient.delete(ENDPOINTS.ADMIN.REGISTRATION(registrationId));
export const bulkDeleteRegistrations = (registrationIds) => apiClient.post(ENDPOINTS.ADMIN.BULK_DELETE_REGISTRATIONS, { registrationIds });

// Score Management
export const getScores = (params) => apiClient.get(ENDPOINTS.ADMIN.SCORES, { params });
export const getScoreById = (scoreId) => apiClient.get(ENDPOINTS.ADMIN.SCORE(scoreId));
export const getScoreByRegistrationId = (registrationId) => apiClient.get(ENDPOINTS.ADMIN.SCORE_BY_REGISTRATION(registrationId));
export const createScore = (data) => apiClient.post(ENDPOINTS.ADMIN.SCORES, data);
export const updateScore = (scoreId, data) => apiClient.put(ENDPOINTS.ADMIN.SCORE(scoreId), data);
export const deleteScore = (scoreId) => apiClient.delete(ENDPOINTS.ADMIN.SCORE(scoreId));

// Exam Management (Admin Enhanced)
export const getAdminExams = (params) => apiClient.get(ENDPOINTS.ADMIN.EXAMS, { params });
export const getAdminExamById = (examId) => apiClient.get(ENDPOINTS.ADMIN.EXAM(examId));
export const createAdminExam = (data) => apiClient.post(ENDPOINTS.ADMIN.EXAMS, data);
export const updateAdminExam = (examId, data) => apiClient.put(ENDPOINTS.ADMIN.EXAM(examId), data);
export const deleteAdminExam = (examId) => apiClient.delete(ENDPOINTS.ADMIN.EXAM(examId));
export const calculateExamScores = (examId) => apiClient.post(ENDPOINTS.ADMIN.EXAM_CALCULATE_SCORES(examId));

// Speaking Session Management
export const getSpeakingSessions = (params) => apiClient.get(ENDPOINTS.ADMIN.SPEAKING_SESSIONS, { params });
export const updateSpeakingSession = (sessionId, data) => apiClient.put(ENDPOINTS.ADMIN.SPEAKING_SESSION(sessionId), data);
export const deleteSpeakingSession = (sessionId) => apiClient.delete(ENDPOINTS.ADMIN.SPEAKING_SESSION(sessionId));

// Referral Management
export const getReferrals = (params) => apiClient.get(ENDPOINTS.ADMIN.REFERRALS, { params });
export const updateReferral = (referralId, data) => apiClient.put(ENDPOINTS.ADMIN.REFERRAL(referralId), data);

// Product Session Management
export const getProductSessions = (params) => apiClient.get(ENDPOINTS.ADMIN.PRODUCT_SESSIONS, { params });
export const getProductSessionById = (sessionId) => apiClient.get(ENDPOINTS.ADMIN.PRODUCT_SESSION(sessionId));
export const updateProductSession = (sessionId, data) => apiClient.put(ENDPOINTS.ADMIN.PRODUCT_SESSION(sessionId), data);
export const deleteProductSession = (sessionId) => apiClient.delete(ENDPOINTS.ADMIN.PRODUCT_SESSION(sessionId));

// User Session Management (Device/Login Sessions)
export const getAllUserSessions = (params) => apiClient.get(ENDPOINTS.USER_SESSIONS.ALL, { params });
export const getUserSessionsByUser = (userId) => apiClient.get(ENDPOINTS.USER_SESSIONS.BY_USER(userId));
export const terminateUserSession = (sessionId) => apiClient.delete(ENDPOINTS.USER_SESSIONS.TERMINATE(sessionId));
export const terminateAllUserSessions = (userId) => apiClient.delete(ENDPOINTS.USER_SESSIONS.TERMINATE_ALL_USER(userId));

// Session Violation Logs
export const getSessionViolations = (params) => apiClient.get(ENDPOINTS.USER_SESSIONS.VIOLATIONS, { params });
export const getRecentViolations = (params) => apiClient.get(ENDPOINTS.USER_SESSIONS.VIOLATIONS_RECENT, { params });
export const getUserViolations = (userId) => apiClient.get(ENDPOINTS.USER_SESSIONS.VIOLATIONS_BY_USER(userId));

// Invoice Management
export const getInvoices = (params) => apiClient.get(ENDPOINTS.ADMIN.INVOICES, { params });
export const getInvoiceById = (invoiceId) => apiClient.get(ENDPOINTS.ADMIN.INVOICE(invoiceId));
export const updateInvoice = (invoiceId, data) => apiClient.put(ENDPOINTS.ADMIN.INVOICE(invoiceId), data);
export const deleteInvoice = (invoiceId) => apiClient.delete(ENDPOINTS.ADMIN.INVOICE(invoiceId));
export const getInvoiceStats = (params) => apiClient.get(ENDPOINTS.ADMIN.INVOICE_STATS, { params });

// ==================== PARTNER MEMBER MANAGEMENT ====================

// Get all partners current user is a member of
export const getMyPartners = () => apiClient.get(ENDPOINTS.PARTNER_MEMBERS.MY_PARTNERS);

// Get available roles and permissions
export const getPartnerRoles = () => apiClient.get(ENDPOINTS.PARTNER_MEMBERS.ROLES);

// Check if user has access to a partner
export const checkPartnerAccess = (partnerId, permission) =>
  apiClient.get(ENDPOINTS.PARTNER_MEMBERS.CHECK_ACCESS(partnerId), { params: { permission } });

// Get members of a partner
export const getPartnerMembers = (partnerId, params) =>
  apiClient.get(ENDPOINTS.PARTNER_MEMBERS.BY_PARTNER(partnerId), { params });

// Get team stats for a partner
export const getPartnerTeamStats = (partnerId) =>
  apiClient.get(ENDPOINTS.PARTNER_MEMBERS.PARTNER_STATS(partnerId));

// Add a new member to a partner
export const addPartnerMember = (data) => apiClient.post(ENDPOINTS.PARTNER_MEMBERS.BASE, data);

// Get a single membership
export const getPartnerMemberById = (membershipId) =>
  apiClient.get(ENDPOINTS.PARTNER_MEMBERS.BY_ID(membershipId));

// Update member role
export const updatePartnerMemberRole = (membershipId, role) =>
  apiClient.put(ENDPOINTS.PARTNER_MEMBERS.UPDATE_ROLE(membershipId), { role });

// Remove a member
export const removePartnerMember = (membershipId) =>
  apiClient.delete(ENDPOINTS.PARTNER_MEMBERS.BY_ID(membershipId));

// Transfer ownership
export const transferPartnerOwnership = (partnerId, toUserId) =>
  apiClient.post(ENDPOINTS.PARTNER_MEMBERS.TRANSFER_OWNERSHIP, { partnerId, toUserId });

// Leave a partner
export const leavePartner = (partnerId) =>
  apiClient.post(ENDPOINTS.PARTNER_MEMBERS.LEAVE(partnerId));

// ==================== PARTNER ADMIN (SCOPED ACCESS) ====================

// Get current user's admin context (partners, permissions)
export const getPartnerAdminContext = () => apiClient.get(ENDPOINTS.PARTNER_ADMIN.CONTEXT);

// Get list of accessible partners
export const getPartnerAdminPartners = (params) =>
  apiClient.get(ENDPOINTS.PARTNER_ADMIN.PARTNERS, { params });

// Get aggregated statistics
export const getPartnerAdminStatistics = () => apiClient.get(ENDPOINTS.PARTNER_ADMIN.STATISTICS);

// Get students (attributed users)
export const getPartnerAdminStudents = (params) =>
  apiClient.get(ENDPOINTS.PARTNER_ADMIN.STUDENTS, { params });

// Get single student details
export const getPartnerAdminStudentById = (attributionId) =>
  apiClient.get(ENDPOINTS.PARTNER_ADMIN.STUDENT(attributionId));

// Classify student (INTERNAL/EXTERNAL)
export const classifyPartnerStudent = (attributionId, userType) =>
  apiClient.patch(ENDPOINTS.PARTNER_ADMIN.CLASSIFY_STUDENT(attributionId), { userType });

// Get registrations for partner students
export const getPartnerAdminRegistrations = (params) =>
  apiClient.get(ENDPOINTS.PARTNER_ADMIN.REGISTRATIONS, { params });


// Get scores for partner students
export const getPartnerAdminScores = (params) =>
  apiClient.get(ENDPOINTS.PARTNER_ADMIN.SCORES, { params });

// Update a score for a partner student
export const updatePartnerAdminScore = (scoreId, data) =>
  apiClient.put(ENDPOINTS.PARTNER_ADMIN.SCORE(scoreId), data);

// Get exams where partner students are registered
export const getPartnerAdminExams = (params) =>
  apiClient.get(ENDPOINTS.PARTNER_ADMIN.EXAMS, { params });

// Get reading answers for partner students
export const getPartnerAdminReadingAnswers = (params) =>
  apiClient.get(ENDPOINTS.PARTNER_ADMIN.READING_ANSWERS, { params });

// Get listening answers for partner students
export const getPartnerAdminListeningAnswers = (params) =>
  apiClient.get(ENDPOINTS.PARTNER_ADMIN.LISTENING_ANSWERS, { params });

// Get writing answers for partner students
export const getPartnerAdminWritingAnswers = (params) =>
  apiClient.get(ENDPOINTS.PARTNER_ADMIN.WRITING_ANSWERS, { params });

// ==================== PARTNER CRUD ====================

// Get all partners
export const getPartners = (params) => apiClient.get(ENDPOINTS.PARTNER.BASE, { params });

// Get active partners
export const getActivePartners = () => apiClient.get(ENDPOINTS.PARTNER.ACTIVE);

// Get partner by ID
export const getPartnerById = (partnerId) => apiClient.get(ENDPOINTS.PARTNER.BY_ID(partnerId));

// Create partner
export const createPartner = (data) => apiClient.post(ENDPOINTS.PARTNER.BASE, data);

// Update partner
export const updatePartner = (partnerId, data) => apiClient.put(ENDPOINTS.PARTNER.BY_ID(partnerId), data);

// Delete partner
export const deletePartner = (partnerId) => apiClient.delete(ENDPOINTS.PARTNER.BY_ID(partnerId));
